import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * Newsletter signup popup.
 *
 * Opens once per page view after a delay, and
 * stays away once the visitor has subscribed on this browser or dismissed it
 * within the configured window. The signed-in check happens in Liquid: the
 * section renders nothing for customers.
 *
 * Submission is a native POST through Shopify's customer form, so the page
 * reloads. A short-lived session flag records that this popup started the
 * submission, which is how the reloaded page knows to reopen it with the
 * success or error message instead of staying quiet (another signup form on
 * the page produces the same Liquid state).
 *
 * Before the post, the browser's own validation is replaced with an inline
 * message: the native bubble is suppressed and the error row under the field
 * is filled instead. The submit button shows a busy state until the page
 * unloads, and comes back to rest if the visitor returns through the
 * back/forward cache.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} [emailInput]
 * @property {HTMLButtonElement} [submitButton]
 * @property {HTMLElement} [submitLabel]
 * @property {HTMLElement} [successMessage]
 * @property {HTMLElement} [errorMessage]
 * @property {HTMLElement} [errorText]
 *
 * @extends DialogComponent<Refs>
 */

const STORAGE_KEY = 'cj-newsletter-popup';
const PENDING_KEY = 'cj-newsletter-popup-pending';
const PENDING_TTL_MS = 15 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const OTHER_DIALOG_RETRY_MS = 3000;
const OTHER_DIALOG_MAX_RETRIES = 10;
const SUCCESS_AUTO_CLOSE_MS = 8000;

/** @typedef {{ status: 'subscribed' | 'dismissed', at: number }} StoredState */

class NewsletterPopup extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;

  /** @type {'close_button' | 'escape' | 'backdrop' | 'submitted' | null} */
  #outcome = null;

  #otherDialogRetries = 0;

  #submitting = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #autoCloseTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('keydown', this.#rememberEscape, true);
    this.refs.dialog.addEventListener('click', this.#rememberBackdropClick);
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit);
    this.refs.emailInput?.addEventListener('invalid', this.#handleInvalid);
    this.refs.emailInput?.addEventListener('input', this.#clearError);
    window.addEventListener('pageshow', this.#handlePageShow);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect);
      return;
    }

    if (this.#handleReturnFromPost()) return;
    // A fresh flag with no result on this page means Shopify sent the visitor
    // through its challenge page first. Stay out of the way until they are back.
    if (this.#hasFreshPendingFlag()) return;
    if (!this.#shouldOpen()) return;

    const delayMs = Number(this.dataset.delay ?? 0) * 1000;
    this.#openTimer = setTimeout(this.#open, delayMs);
  }

  disconnectedCallback() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#autoCloseTimer);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener('visibilitychange', this.#openWhenVisible);
    document.removeEventListener('shopify:section:select', this.#handleEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleEditorDeselect);
    super.disconnectedCallback();
  }

  /**
   * Close button handler, wired from Liquid with `on:click="/dismiss"`.
   */
  dismiss() {
    this.#outcome = 'close_button';
    this.closeDialog();
  }

  /**
   * Reopens the popup after Shopify reloaded the page following a submission.
   *
   * @returns {boolean} True when the page is a post-submission reload, so the
   *   normal delayed open must not run.
   */
  #handleReturnFromPost() {
    const { successMessage, errorMessage } = this.refs;
    const posted = Boolean(successMessage);
    const failed = Boolean(errorMessage) && !errorMessage.hidden;

    if (!posted && !failed) return false;

    const startedHere = this.#takePendingFlag();

    if (posted) {
      // A signup from any form on this browser counts: the popup never returns.
      this.#writeState({ status: 'subscribed', at: Date.now() });
    }

    // Another form on the page posted: record it, but keep quiet this page view.
    if (!startedHere) return true;

    this.#outcome = posted ? 'submitted' : null;
    this.#openNow();

    if (posted) {
      this.#capture('Newsletter Popup Subscribed');
      this.#autoCloseTimer = setTimeout(this.closeDialog, SUCCESS_AUTO_CLOSE_MS);
    } else {
      this.refs.emailInput?.focus();
      this.refs.emailInput?.select();
    }

    return true;
  }

  /**
   * Whether the stored state allows the popup to open.
   *
   * @returns {boolean}
   */
  #shouldOpen() {
    const state = this.#readState();
    if (!state) return true;
    if (state.status === 'subscribed') return false;

    const dismissDays = Number(this.dataset.dismissDays ?? 30);
    return Date.now() - state.at > dismissDays * DAY_MS;
  }

  #open = () => {
    if (document.visibilityState === 'hidden') {
      document.addEventListener('visibilitychange', this.#openWhenVisible, { once: true });
      return;
    }

    if (this.#anotherDialogIsOpen()) {
      if (this.#otherDialogRetries >= OTHER_DIALOG_MAX_RETRIES) return;
      this.#otherDialogRetries += 1;
      this.#openTimer = setTimeout(this.#open, OTHER_DIALOG_RETRY_MS);
      return;
    }

    this.#openNow();
    this.#capture('Newsletter Popup Shown');
  };

  #openWhenVisible = () => {
    if (document.visibilityState !== 'visible') return;
    this.#open();
  };

  #openNow() {
    this.showDialog();
  }

  /**
   * @returns {boolean}
   */
  #anotherDialogIsOpen() {
    for (const dialog of document.querySelectorAll('dialog[open]')) {
      if (dialog !== this.refs.dialog) return true;
    }
    return false;
  }

  /**
   * Runs once the browser has accepted the form. The native POST follows, so
   * the busy state stays until the page unloads.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.checkValidity()) return;

    if (this.#submitting) {
      event.preventDefault();
      return;
    }

    this.#submitting = true;
    this.#setBusy(true);
    this.#outcome = 'submitted';
    this.#writePendingFlag();
    // The page navigates straight after this, so hand the event to the beacon API.
    this.#capture('Newsletter Popup Submitted', {}, { transport: 'sendBeacon' });
  };

  /**
   * Replaces the browser's validation bubble with the inline error row.
   *
   * @param {Event} event
   */
  #handleInvalid = (event) => {
    event.preventDefault();

    const input = this.refs.emailInput;
    if (!input) return;

    this.#showError(input.dataset.invalidMessage ?? '');
    input.focus();
  };

  #clearError = () => {
    const { emailInput, errorMessage, errorText } = this.refs;
    if (!errorMessage || errorMessage.hidden) return;

    errorMessage.hidden = true;
    if (errorText) errorText.textContent = '';
    emailInput?.removeAttribute('aria-invalid');
  };

  /**
   * @param {string} message
   */
  #showError(message) {
    const { emailInput, errorMessage, errorText } = this.refs;
    if (!errorMessage || !errorText) return;

    errorText.textContent = message;
    errorMessage.hidden = false;
    emailInput?.setAttribute('aria-invalid', 'true');
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    const { submitButton, submitLabel } = this.refs;
    if (!submitButton || !submitLabel) return;

    const { busyLabel } = submitButton.dataset;

    if (busy) {
      submitButton.dataset.restingLabel ??= submitLabel.textContent ?? '';
      submitButton.setAttribute('aria-busy', 'true');
      if (busyLabel) submitLabel.textContent = busyLabel;
      return;
    }

    submitButton.removeAttribute('aria-busy');
    submitLabel.textContent = submitButton.dataset.restingLabel ?? submitLabel.textContent;
  }

  /**
   * The back/forward cache restores the page as it was mid-submission.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    this.#submitting = false;
    this.#setBusy(false);
  };

  #handleDialogClose = () => {
    clearTimeout(this.#autoCloseTimer);
    if (this.#outcome === 'submitted') return;
    if (window.Shopify?.designMode) return;

    const method = this.#outcome ?? 'escape';
    this.#outcome = null;

    const state = this.#readState();
    if (state?.status === 'subscribed') return;

    this.#writeState({ status: 'dismissed', at: Date.now() });
    this.#capture('Newsletter Popup Dismissed', { method });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #rememberEscape = (event) => {
    if (event.key === 'Escape') this.#outcome = 'escape';
  };

  /**
   * A click on the backdrop targets the dialog element itself.
   *
   * @param {MouseEvent} event
   */
  #rememberBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.#outcome = 'backdrop';
  };

  /**
   * @param {Event} event
   */
  #handleEditorSelect = (event) => {
    if (!this.#isEditorEventForThisSection(event)) return;
    this.showDialog();
  };

  /**
   * @param {Event} event
   */
  #handleEditorDeselect = (event) => {
    if (!this.#isEditorEventForThisSection(event)) return;
    this.closeDialog();
  };

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #isEditorEventForThisSection(event) {
    const detail = /** @type {CustomEvent<{ sectionId?: string }>} */ (event).detail;
    return detail?.sectionId === this.dataset.sectionId;
  }

  /**
   * @returns {StoredState | null}
   */
  #readState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.status !== 'subscribed' && parsed?.status !== 'dismissed') return null;
      return { status: parsed.status, at: Number(parsed.at) || 0 };
    } catch {
      return null;
    }
  }

  /**
   * @param {StoredState} state
   */
  #writeState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage unavailable (private mode, blocked): the popup simply shows again.
    }
  }

  #writePendingFlag() {
    try {
      sessionStorage.setItem(PENDING_KEY, String(Date.now()));
    } catch {
      // Without the flag the reloaded page records the subscription but stays quiet.
    }
  }

  /**
   * @returns {boolean} True when this popup submitted within the last few minutes.
   */
  #hasFreshPendingFlag() {
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) return false;
      return Date.now() - Number(raw) < PENDING_TTL_MS;
    } catch {
      return false;
    }
  }

  /**
   * Reads and clears the submission flag.
   *
   * @returns {boolean} True when this popup submitted within the last few minutes.
   */
  #takePendingFlag() {
    const fresh = this.#hasFreshPendingFlag();
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch {
      // Nothing to clear.
    }
    return fresh;
  }

  /**
   * @param {string} eventName
   * @param {Record<string, unknown>} [properties]
   * @param {Record<string, unknown>} [options]
   */
  #capture(eventName, properties = {}, options) {
    window.posthog?.capture(
      eventName,
      {
        variant: this.dataset.variant,
        page_type: this.dataset.pageType,
        ...properties,
      },
      options
    );
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
